import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { type LifecycleStage } from '@/constants/lifecycleStages';
import { buildQuantityLabel, parseWarrantyMonths } from '@/utils/lifecycleQuantity';
import type { Project } from '@/types/project';
import InlineNumberCell from './InlineNumberCell';
import InlineDateCell from './InlineDateCell';
import InlineStageCell from './InlineStageCell';
import WatchSubstatusCell from './WatchSubstatusCell';
import type { LifecycleWatchSubstatus } from '@/constants/lifecycleStages';
import { useUpdateProjectBaseline, type BaselineField, type BaselineValue } from '@/hooks/maintenance/useUpdateProjectBaseline';
import { useCanOverrideLifecycle } from '@/hooks/maintenance/useCanOverrideLifecycle';
import { useProjectsEditPermissions } from '@/hooks/maintenance/useProjectsEditPermissions';
import { useCheckPermission } from '@/hooks/useUserRoles';
import { useIsMobile } from '@/hooks/use-mobile';

interface Props {
  projects: Project[];
  isLoading?: boolean;
}

const COL_COUNT = 8;

export default function MaintenanceProjectTable({ projects, isLoading }: Props) {
  const navigate = useNavigate();
  const canOverride = useCanOverrideLifecycle();
  const isMobile = useIsMobile();
  const mutation = useUpdateProjectBaseline();

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { data: editMap = {} } = useProjectsEditPermissions(projectIds);
  const hasGfaKey = useCheckPermission('PROJECTS_EDIT_GFA').data ?? false;

  const save = (projectId: string, field: BaselineField) => async (value: BaselineValue) => {
    await mutation.mutateAsync({ projectId, field, value });
  };

  return (
    <Card className="overflow-x-auto">
      <Table className="min-w-[1100px]">
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[220px]">Tên dự án</TableHead>
            <TableHead className="min-w-[120px]">Stage</TableHead>
            <TableHead className="min-w-[130px]">Quantity</TableHead>
            <TableHead className="min-w-[140px]">Ngày bàn giao</TableHead>
            <TableHead className="min-w-[110px]">Warranty (tháng)</TableHead>
            <TableHead className="min-w-[110px]">GFA (m²)</TableHead>
            <TableHead className="min-w-[110px]">Manhour</TableHead>
            <TableHead className="min-w-[140px]">Company</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: COL_COUNT }).map((__, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : projects.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COL_COUNT} className="text-center text-muted-foreground py-8">
                Không tìm thấy dự án nào
              </TableCell>
            </TableRow>
          ) : (
            projects.map((p) => {
              const stage = p.lifecycle_stage as LifecycleStage | null | undefined;
              const months = parseWarrantyMonths(p.warranty_period as any);
              return (
                <TableRow key={p.id}>
                  <TableCell className="py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/projects/${p.id}`)}
                      className="flex items-center gap-3 text-left cursor-pointer hover:underline"
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          loading="lazy"
                          className="w-14 h-14 rounded object-contain bg-muted/40 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded bg-muted flex-shrink-0" />
                      )}
                      <span className="font-medium truncate">{p.project_name}</span>
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5 flex-wrap items-center">
                      <InlineStageCell
                        projectId={p.id}
                        stage={stage}
                        isOverridden={(p as any).lifecycle_stage_override != null}
                        editable={canOverride && !isMobile}
                      />
                      <WatchSubstatusCell
                        projectId={p.id}
                        value={((p as any).watch_substatus as LifecycleWatchSubstatus | null) ?? null}
                        editable={canOverride && !isMobile}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {stage
                      ? buildQuantityLabel(
                          stage,
                          p.cost_driver,
                          p.display_quantity_hour,
                          p.display_quantity_m2,
                        )
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <InlineDateCell
                      value={p.handover_date ?? null}
                      onSave={save(p.id, 'handover_date')}
                      editable={editMap[p.id] === true && !isMobile}
                    />
                  </TableCell>
                  <TableCell>
                    <InlineNumberCell
                      value={months}
                      onSave={save(p.id, 'warranty_period')}
                      editable={editMap[p.id] === true && !isMobile}
                      min={1}
                      allowDecimal={false}
                      allowNull={false}
                    />
                  </TableCell>
                  <TableCell>
                    <InlineNumberCell
                      value={p.gross_floor_area_m2 ?? null}
                      onSave={save(p.id, 'gross_floor_area_m2')}
                      editable={editMap[p.id] === true && hasGfaKey && !isMobile}
                      min={0}
                      allowDecimal={true}
                      allowNull={true}
                    />
                  </TableCell>
                  <TableCell>
                    <InlineNumberCell
                      value={p.total_manhour_estimate ?? null}
                      onSave={save(p.id, 'total_manhour_estimate')}
                      editable={editMap[p.id] === true && !isMobile}
                      min={0}
                      allowDecimal={true}
                      allowNull={true}
                    />
                  </TableCell>
                  <TableCell className="text-sm">{p.company || '—'}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

